import {
    ServiceStatisticsGranularity,
    ServiceStatisticsHourlyDTO,
    ServiceStatisticsMetricsDTO,
    ServiceStatisticsOccupancyDTO,
    ServiceStatisticsOccupancyMetricsDTO,
    ServiceStatisticsServiceDTO,
    ServiceStatisticsTimelineRowDTO,
} from '../../dto/ServiceStatisticsDTO';
import { addDays, isoDate } from '../../components/novarium/periodRange';
import {
    ALERT_HIGH_OVERRUN_MINUTES, ALERT_MAX_OVERBOOKED_PERCENT, ALERT_MIN_ATTENTIONS, ALERT_MIN_OVERRUN_DAYS, ALERT_MIN_OVERRUN_SHARE,
    ALERT_SHORT_SLOT_RATIO, CRITICAL_HOURS_CANDIDATES, CRITICAL_HOURS_MAX_SHOWN, CRITICAL_HOURS_MIN_ATTENTIONS,
    MIN_PREVIOUS_FOR_WAIT_CHANGE, SLOT_ROUNDING_MINUTES,
} from './alertThresholds';

/** Qué se mide en los gráficos: el mismo dato en otra vista. */
export type Measure = 'attentions' | 'wait' | 'service';

/** Valor de los selects para "Todos" (los ids reales son mayores que cero). */
export const ALL = 0;

/** Hasta esta cantidad de días el gráfico de evolución muestra un punto por día. */
export const MAX_DAYS_BY_DAY = 31;
/** Hasta esta cantidad de días, un punto por semana; con más, uno por mes. */
export const MAX_DAYS_BY_WEEK = 120;

/** Las agrupaciones de la evolución en el orden en que se ofrecen en "Agrupar por". */
export const GRANULARITIES: ServiceStatisticsGranularity[] = ['DAY', 'WEEK', 'MONTH'];

/** La agrupación que se elige sola según el largo del rango, para que el gráfico se pueda leer. */
export function autoGranularity(days: number): ServiceStatisticsGranularity {
    if (days <= MAX_DAYS_BY_DAY) return 'DAY';
    return days <= MAX_DAYS_BY_WEEK ? 'WEEK' : 'MONTH';
}

/** Las medidas en el orden en que se ofrecen en "Medir por". Los nombres están en `es.json` (`ServicesStatisticsMeasure…`). */
export const MEASURES: Measure[] = ['attentions', 'wait', 'service'];

// ── Formato ──

const integerFormat = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 });
const decimalFormat = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export const formatInt = (value: number): string => integerFormat.format(value);

export const formatDecimal = (value: number): string => decimalFormat.format(value);

export const formatMinutes = (value: number | null | undefined): string =>
    value === null || value === undefined ? '—' : `${decimalFormat.format(value)} min`;

/** `ratio` es una fracción (0,25 = 25 %). */
export function formatPercent(ratio: number, digits = 0): string {
    const text = (ratio * 100).toLocaleString('es-AR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
    return `${text} %`;
}

export const formatHour = (hour: number | null | undefined): string =>
    hour === null || hour === undefined ? '—' : `${String(hour).padStart(2, '0')}:00`;

/** Variación relativa contra el período anterior (fracción), o null si no hay con qué comparar. */
export function relativeChange(current: number, previous: number | null | undefined): number | null {
    if (previous === null || previous === undefined || !(previous > 0)) return null;
    return (current - previous) / previous;
}

/**
 * Cantidad de sobreturnos de un período. Si el backend todavía no manda el campo, cuenta como cero.
 */
export const overbookedOf = (metrics: { overbooked?: number | null }): number => metrics.overbooked ?? 0;

/** Variación en puntos porcentuales entre dos proporciones (fracciones), con signo: "+2,1". La unidad la pone el mensaje. */
export function formatSignedPoints(current: number, previous: number): string {
    const points = (current - previous) * 100;
    const text = Math.abs(points).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    return `${points >= 0 ? '+' : '−'}${text}`;
}

/** Variación escrita con signo ("+12 %", "−8 %"): no depende del color. */
export function formatSignedPercent(change: number): string {
    return `${change >= 0 ? '+' : '−'}${formatPercent(Math.abs(change))}`;
}

// ── Fechas ──

// El período (atajos, rango y fechas cortas) es de `components/novarium/periodRange`: lo comparte «Atenciones de urgencia»
export { activePreset, addDays, formatShortDate, isoDate, PERIOD_PRESETS, periodNeedsYear, presetRange } from '../../components/novarium/periodRange';
export type { PeriodPreset } from '../../components/novarium/periodRange';

/** Fecha local a partir de `yyyy-MM-dd`. */
export function parseIsoDate(iso: string): Date {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day);
}

/** Cantidad de días del rango, con los dos extremos incluidos. */
export function countDays(from: Date, to: Date): number {
    const start = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
    const end = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
    return Math.round((end - start) / 86400000) + 1;
}

// ── Evolución ──

export interface TimelinePoint {
    /** Clave del período en el backend: el día, el lunes de la semana (`yyyy-MM-dd`) o el mes (`yyyy-MM`). */
    period: string;
    /** Primer día del período (el día, el lunes o el día 1), a medianoche local: es la posición en el eje de tiempo. */
    start: Date;
    /** Último día del período: el mismo día, el domingo o el último día del mes (aunque el rango termine antes). */
    end: Date;
    attentions: number;
    /** Null si el período no tuvo atenciones: no se dibuja (no cuenta como cero minutos). */
    avgWait: number | null;
    avgService: number | null;
}

/** Lunes de la semana de `date`. */
export function mondayOf(date: Date): Date {
    const sinceMonday = (date.getDay() + 6) % 7;
    return addDays(date, -sinceMonday);
}

/**
 * Un punto por cada día, semana (de lunes a domingo) o mes del rango, también los que no tuvieron atenciones (el backend
 * no los manda). Las semanas y los meses de los extremos se muestran enteros, aunque el rango empiece o termine a mitad.
 */
export function buildTimeline(rows: ServiceStatisticsTimelineRowDTO[], initialDate: string, finalDate: string,
                              granularity: ServiceStatisticsGranularity): TimelinePoint[] {
    const byPeriod = new Map(rows.map(row => [row.period, row]));
    const from = parseIsoDate(initialDate);
    const to = parseIsoDate(finalDate);
    const points: TimelinePoint[] = [];

    const push = (period: string, start: Date, end: Date) => {
        const row = byPeriod.get(period);
        points.push({
            period,
            start,
            end,
            attentions: row?.attentions ?? 0,
            avgWait: row ? row.avgWaitMinutes : null,
            avgService: row ? row.avgServiceMinutes : null,
        });
    };

    if (granularity === 'DAY') {
        for (let date = from; date <= to; date = addDays(date, 1)) push(isoDate(date), date, date);
    } else if (granularity === 'WEEK') {
        for (let monday = mondayOf(from); monday <= to; monday = addDays(monday, 7)) {
            push(isoDate(monday), monday, addDays(monday, 6));
        }
    } else {
        for (let date = new Date(from.getFullYear(), from.getMonth(), 1); date <= to;
             date = new Date(date.getFullYear(), date.getMonth() + 1, 1)) {
            const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            push(period, date, new Date(date.getFullYear(), date.getMonth() + 1, 0));
        }
    }
    return points;
}

// ── Curva por hora y mapa de calor ──

export interface HourPoint {
    hour: number;
    attentions: number;
    /** Pacientes por día con atención (no tiene sentido sin atenciones). */
    perDay: number | null;
    avgWait: number | null;
    avgService: number | null;
}

interface Accumulator { attentions: number; sumWait: number; sumService: number }

function toPoint(hour: number, acc: Accumulator | undefined, days: number): HourPoint {
    if (!acc || acc.attentions <= 0) return { hour, attentions: 0, perDay: null, avgWait: null, avgService: null };
    return {
        hour,
        attentions: acc.attentions,
        perDay: acc.attentions / Math.max(days, 1),
        avgWait: acc.sumWait / acc.attentions,
        avgService: acc.sumService / acc.attentions,
    };
}

function addTo(map: Map<number, Accumulator>, key: number, attentions: number, sumWait: number, sumService: number) {
    const acc = map.get(key) ?? { attentions: 0, sumWait: 0, sumService: 0 };
    acc.attentions += attentions;
    acc.sumWait += sumWait;
    acc.sumService += sumService;
    map.set(key, acc);
}

/**
 * Las 24 horas del día sumando todos los servicios del filtro, que es lo mismo que suma el mapa de calor. Los pacientes
 * por día son las atenciones de la hora sobre los días con alguna atención (la suma de los días de la semana), como la
 * hora pico del resumen.
 */
export function buildHourlyTotal(data: ServiceStatisticsHourlyDTO): HourPoint[] {
    const hours = new Map<number, Accumulator>();
    for (const row of data.rows) addTo(hours, row.hour, row.attentions, row.sumWaitMinutes, row.sumServiceMinutes);
    const days = data.weekdayDays.reduce((sum, item) => sum + item.days, 0);
    return Array.from({ length: 24 }, (_, hour) => toPoint(hour, hours.get(hour), days));
}

/** De la primera a la última hora con atenciones; null si no hay ninguna. */
export function activeHourRange(points: HourPoint[]): { from: number; to: number } | null {
    const active = points.filter(point => point.attentions > 0).map(point => point.hour);
    return active.length > 0 ? { from: Math.min(...active), to: Math.max(...active) } : null;
}

/** Matriz de 7 días (lunes a domingo) por 24 horas, sumando todos los servicios. */
export function buildHeat(data: ServiceStatisticsHourlyDTO): HourPoint[][] {
    const daysByWeekday = new Map(data.weekdayDays.map(item => [item.dayOfWeek, item.days]));
    const cells = new Map<number, Accumulator>();
    for (const row of data.rows) {
        const key = row.dayOfWeek * 100 + row.hour;
        const acc = cells.get(key) ?? { attentions: 0, sumWait: 0, sumService: 0 };
        acc.attentions += row.attentions;
        acc.sumWait += row.sumWaitMinutes;
        acc.sumService += row.sumServiceMinutes;
        cells.set(key, acc);
    }
    return Array.from({ length: 7 }, (_, index) => {
        const dayOfWeek = index + 1;
        return Array.from({ length: 24 }, (_, hour) =>
            toPoint(hour, cells.get(dayOfWeek * 100 + hour), daysByWeekday.get(dayOfWeek) ?? 1));
    });
}

export function measureValue(point: HourPoint, measure: Measure): number | null {
    if (measure === 'attentions') return point.perDay;
    return measure === 'wait' ? point.avgWait : point.avgService;
}

// ── Ocupación de las agendas ──

/** Sumas de las agendas elegidas. Los porcentajes se calculan sobre las sumas, nunca promediando porcentajes. */
export interface OccupancyTotals {
    slots: number;
    booked: number;
    overbooked: number;
    missed: number;
    cancelled: number;
    /** Fracción (0,7 = 70 %); null si no hubo lugares. */
    occupancy: number | null;
    /** Ausentes sobre turnos que debían asistir (agendados, atendidos y ausentes); null si no hubo. */
    missedRate: number | null;
}

export function sumOccupancy(list: ServiceStatisticsOccupancyMetricsDTO[]): OccupancyTotals {
    const total = { slots: 0, booked: 0, overbooked: 0, missed: 0, cancelled: 0 };
    for (const item of list) {
        total.slots += item.slots ?? 0;
        total.booked += item.booked ?? 0;
        total.overbooked += item.overbooked ?? 0;
        total.missed += item.missed ?? 0;
        total.cancelled += item.cancelled ?? 0;
    }
    return {
        ...total,
        occupancy: total.slots > 0 ? total.booked / total.slots : null,
        missedRate: total.booked + total.missed > 0 ? total.missed / (total.booked + total.missed) : null,
    };
}

/**
 * Totales de las agendas y, para compararlos con el período anterior, los totales de las agendas que tienen período
 * anterior (en el actual y en el anterior): así no se mezclan agendas nuevas con viejas.
 */
export interface OccupancyComparison {
    totals: OccupancyTotals;
    comparableCurrent: OccupancyTotals;
    previous: OccupancyTotals;
}

export function compareOccupancy(list: ServiceStatisticsOccupancyDTO[]): OccupancyComparison {
    const comparable = list.filter(item => item.previous !== null);
    return {
        totals: sumOccupancy(list.map(item => item.current)),
        comparableCurrent: sumOccupancy(comparable.map(item => item.current)),
        previous: sumOccupancy(comparable.map(item => item.previous!)),
    };
}

/** Ocupación de una agenda (fracción), o null si no ofreció lugares. */
export const occupancyOf = (metrics: ServiceStatisticsOccupancyMetricsDTO): number | null =>
    (metrics.slots > 0 ? metrics.booked / metrics.slots : null);

/** Las agendas que entran en la sección: la elegida si hay una (si no es un consultorio, ninguna) o todas. */
export function scopeOccupancy(list: ServiceStatisticsOccupancyDTO[], serviceId: number): ServiceStatisticsOccupancyDTO[] {
    return serviceId ? list.filter(item => item.serviceId === serviceId) : list;
}

// ── Tabla de servicios ──

/** Una agenda de consultorio cuya consulta dura más que su turno: los minutos programados, los reales y el turno que conviene. */
export interface ShortSlot {
    slotMinutes: number;
    durationMinutes: number;
    suggestedMinutes: number;
}

/**
 * Turno más corto que la consulta: la duración media real es al menos `ALERT_SHORT_SLOT_RATIO` veces el turno
 * programado de la agenda (solo consultorios con `slotMinutes`), con el mínimo de atenciones de `ALERT_MIN_ATTENTIONS`
 * para que no sea ruido. El turno sugerido es la duración media redondeada hacia arriba a múltiplos de
 * `SLOT_ROUNDING_MINUTES`, y nunca menos que el turno actual más ese salto. No mira la espera: eso lo agrega la alerta.
 */
export function shortSlotOf(metrics: Pick<ServiceStatisticsMetricsDTO, 'attentions' | 'avgServiceMinutes' | 'slotMinutes'>):
    ShortSlot | null {
    const slot = metrics.slotMinutes;
    if (!slot || slot <= 0 || metrics.attentions < ALERT_MIN_ATTENTIONS) return null;
    const duration = metrics.avgServiceMinutes;
    // La tolerancia evita que 10 × 1,1 = 11,000000000000002 deje afuera una duración de exactamente 11
    if (!(duration >= slot * ALERT_SHORT_SLOT_RATIO - 1e-9)) return null;
    return {
        slotMinutes: slot,
        durationMinutes: duration,
        suggestedMinutes: Math.max(slot + SLOT_ROUNDING_MINUTES,
            Math.ceil(duration / SLOT_ROUNDING_MINUTES) * SLOT_ROUNDING_MINUTES),
    };
}

/** Una agenda de consultorio que se pasa del horario: en cuántas de sus jornadas se terminó de atender tarde y cuánto. */
export interface Overrun {
    /** Jornadas en que se terminó de atender después del cierre de la agenda. */
    days: number;
    /** Jornadas con atenciones. */
    scheduleDays: number;
    avgMinutes: number;
    maxMinutes: number;
    /** Alta si el exceso medio llega a `ALERT_HIGH_OVERRUN_MINUTES`; si no, media. */
    severity: 'warn' | 'err';
}

/**
 * Se pasa del horario: al menos `ALERT_MIN_OVERRUN_DAYS` jornadas en que se terminó de atender tarde, que son al menos
 * `ALERT_MIN_OVERRUN_SHARE` de las jornadas con atenciones, con `ALERT_MIN_ATTENTIONS` atenciones o más para que no sea
 * ruido. Solo hay datos de consultorios (servicios y sin dato vienen en cero, y si el backend todavía no manda los campos
 * se leen como cero), así que ahí devuelve null.
 */
export function overrunOf(metrics: Pick<ServiceStatisticsMetricsDTO,
    'attentions' | 'scheduleDays' | 'overrunDays' | 'avgOverrunMinutes' | 'maxOverrunMinutes'>): Overrun | null {
    const days = metrics.overrunDays ?? 0;
    const scheduleDays = metrics.scheduleDays ?? 0;
    if (metrics.attentions < ALERT_MIN_ATTENTIONS || scheduleDays <= 0 || days < ALERT_MIN_OVERRUN_DAYS
        || days / scheduleDays < ALERT_MIN_OVERRUN_SHARE) return null;
    const avgMinutes = metrics.avgOverrunMinutes ?? 0;
    return {
        days,
        scheduleDays,
        avgMinutes,
        maxMinutes: metrics.maxOverrunMinutes ?? avgMinutes,
        severity: avgMinutes >= ALERT_HIGH_OVERRUN_MINUTES ? 'err' : 'warn',
    };
}

export interface ServiceRow {
    serviceId: number;
    serviceName: string;
    attentions: number;
    avgWait: number;
    /** Variación de la espera contra el período anterior (fracción); null si el anterior no tiene datos suficientes. */
    waitChange: number | null;
    avgService: number;
    /** Largo del turno programado, en minutos; null en servicios y sin dato. */
    slotMinutes: number | null;
    /** Con datos si el turno es más corto que la consulta (ver `shortSlotOf`); null si no. */
    shortSlot: ShortSlot | null;
    /** Con datos si la agenda termina tarde (ver `overrunOf`); null si no. */
    overrun: Overrun | null;
    /** Proporción de sobreturnos (fracción) si supera el máximo de `ALERT_MAX_OVERBOOKED_PERCENT`; null si no. */
    overbookedRatio: number | null;
    /** Ocupación de la agenda en %, o null (guardias y agendas sin lugares). */
    occupancyPercent: number | null;
    /** Ausentismo de la agenda en %, o null (servicios sin agenda con lugares y agendas sin turnos que debían asistir). */
    missedPercent: number | null;
}

/** Ausentes sobre turnos que debían asistir (agendados, atendidos y ausentes), como fracción; null si no hubo. */
export const missedRateOf = (metrics: ServiceStatisticsOccupancyMetricsDTO): number | null => {
    const expected = (metrics.booked ?? 0) + (metrics.missed ?? 0);
    return expected > 0 ? (metrics.missed ?? 0) / expected : null;
};

export function toServiceRow(service: ServiceStatisticsServiceDTO, occupancy?: ServiceStatisticsOccupancyDTO): ServiceRow {
    const { current, previous } = service;
    const comparable = previous !== null && previous.attentions >= MIN_PREVIOUS_FOR_WAIT_CHANGE;
    const occupancyRatio = occupancy ? occupancyOf(occupancy.current) : null;
    const missedRate = occupancy ? missedRateOf(occupancy.current) : null;
    return {
        serviceId: service.serviceId,
        serviceName: service.serviceName,
        attentions: current.attentions,
        avgWait: current.avgWaitMinutes,
        waitChange: comparable ? relativeChange(current.avgWaitMinutes, previous.avgWaitMinutes) : null,
        avgService: current.avgServiceMinutes,
        slotMinutes: current.slotMinutes ?? null,
        shortSlot: shortSlotOf(current),
        overrun: overrunOf(current),
        overbookedRatio: current.attentions >= ALERT_MIN_ATTENTIONS
            && overbookedOf(current) / current.attentions > ALERT_MAX_OVERBOOKED_PERCENT / 100
            ? overbookedOf(current) / current.attentions : null,
        occupancyPercent: occupancyRatio === null ? null : occupancyRatio * 100,
        missedPercent: missedRate === null ? null : missedRate * 100,
    };
}

/** Las filas de un servicio o agenda: solo la del elegido si hay uno (`ALL` = todas). */
export function scopeToService<T extends { serviceId: number }>(list: T[], serviceId: number): T[] {
    return serviceId ? list.filter(item => item.serviceId === serviceId) : list;
}

// ── Peores horarios ──

export interface CriticalHour {
    /** 1 = lunes … 7 = domingo. */
    dayOfWeek: number;
    /** Hora de inicio (0 a 23). */
    fromHour: number;
    /** Hora de fin, exclusiva: un solo horario de las 10 va de 10 a 11. */
    toHour: number;
    avgWait: number;
    attentions: number;
}

/**
 * Los horarios (día de la semana y hora) con más espera media, sumando todos los servicios del filtro. Solo cuentan los
 * que tienen `CRITICAL_HOURS_MIN_ATTENTIONS` atenciones; de los `CRITICAL_HOURS_CANDIDATES` peores, los de horas seguidas
 * del mismo día se unen en un tramo (espera media ponderada) y se devuelven los `CRITICAL_HOURS_MAX_SHOWN` peores.
 */
export function buildCriticalHours(data: ServiceStatisticsHourlyDTO): CriticalHour[] {
    const cells = new Map<number, Accumulator>();
    for (const row of data.rows) {
        const key = row.dayOfWeek * 100 + row.hour;
        const acc = cells.get(key) ?? { attentions: 0, sumWait: 0, sumService: 0 };
        acc.attentions += row.attentions;
        acc.sumWait += row.sumWaitMinutes;
        cells.set(key, acc);
    }
    const worst = Array.from(cells, ([key, acc]) => ({
        dayOfWeek: Math.floor(key / 100), hour: key % 100, attentions: acc.attentions, sumWait: acc.sumWait,
    }))
        .filter(cell => cell.attentions >= CRITICAL_HOURS_MIN_ATTENTIONS)
        .sort((a, b) => b.sumWait / b.attentions - a.sumWait / a.attentions)
        .slice(0, CRITICAL_HOURS_CANDIDATES)
        .sort((a, b) => a.dayOfWeek - b.dayOfWeek || a.hour - b.hour);

    const ranges: (CriticalHour & { sumWait: number })[] = [];
    for (const cell of worst) {
        const last = ranges[ranges.length - 1];
        if (last && last.dayOfWeek === cell.dayOfWeek && last.toHour === cell.hour) {
            last.toHour = cell.hour + 1;
            last.attentions += cell.attentions;
            last.sumWait += cell.sumWait;
            last.avgWait = last.sumWait / last.attentions;
        } else {
            ranges.push({
                dayOfWeek: cell.dayOfWeek, fromHour: cell.hour, toHour: cell.hour + 1, attentions: cell.attentions,
                sumWait: cell.sumWait, avgWait: cell.sumWait / cell.attentions,
            });
        }
    }
    return ranges
        .sort((a, b) => b.avgWait - a.avgWait)
        .slice(0, CRITICAL_HOURS_MAX_SHOWN)
        .map(({ dayOfWeek, fromHour, toHour, avgWait, attentions }) => ({ dayOfWeek, fromHour, toHour, avgWait, attentions }));
}

// ── Comparación con la especialidad ──

export type BenchmarkIndicator = 'wait' | 'waitP90' | 'service';

/** Un indicador del profesional contra el promedio de su especialidad, en minutos. */
export interface BenchmarkRow {
    id: BenchmarkIndicator;
    mine: number | null;
    average: number | null;
}

export type BenchmarkTrend = 'less' | 'more' | 'same';

export interface BenchmarkDifference {
    trend: BenchmarkTrend;
    /** Tamaño de la diferencia, siempre positivo, en minutos. */
    amount: number;
}

/** Un tiempo en cero es un dato que no hay (el percentil 90 de un período sin espera registrada). */
const timeOrNull = (minutes: number | null | undefined): number | null =>
    (minutes !== null && minutes !== undefined && minutes > 0 ? minutes : null);

/** Las filas de «Vos y tu especialidad»: espera media, percentil 90 y duración media, en minutos. */
export function buildBenchmarkRows(mine: ServiceStatisticsMetricsDTO, average: ServiceStatisticsMetricsDTO): BenchmarkRow[] {
    return [
        { id: 'wait', mine: timeOrNull(mine.avgWaitMinutes), average: timeOrNull(average.avgWaitMinutes) },
        { id: 'waitP90', mine: timeOrNull(mine.waitP90Minutes), average: timeOrNull(average.waitP90Minutes) },
        { id: 'service', mine: timeOrNull(mine.avgServiceMinutes), average: timeOrNull(average.avgServiceMinutes) },
    ];
}

/**
 * Cuánto se aparta el profesional del promedio, con el redondeo con que se muestra (un decimal): si ese redondeo da
 * cero, es "igual". Null si falta alguno de los dos valores.
 */
export function benchmarkDifference(row: BenchmarkRow): BenchmarkDifference | null {
    if (row.mine === null || row.average === null) return null;
    const amount = Math.round(Math.abs(row.mine - row.average) * 10) / 10;
    if (amount === 0) return { trend: 'same', amount };
    return { trend: row.mine < row.average ? 'less' : 'more', amount };
}

export function escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
